"use client"
import Text from "@/app/(site)/_components/Atoms/Text"
import ColorSwatches from "@/app/(site)/_components/Page/Product/ProductDetail/ColorSwatches"
import SizeChart from "@/app/(site)/_components/Page/Product/ProductDetail/SizeChart"
import {
    useEyeFitUIEvent,
    type EyeFitUInitData
} from "@/app/(site)/_utils/_hooks/useEyeFitUIEvent"
import { useProductStore } from "@/app/(site)/contexts/Global"
import { viewItemDepictEvent } from "@/lib/utils/tracking/depictEvents"
import { t } from "@/translations"
import {
    type CheckoutAPIProduct,
    type RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import placeholder from "@assets/images/placeholder.png"
import { usePathname, useRouter } from "next/navigation"
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from "react"

import GCTextElement from "@/app/(site)/_components/GCMolecules/GCTextElement"
import { ProductUsps } from "@/app/(site)/_components/Molecules/ProductUsps"
import { Anchors } from "@/app/(site)/_components/Page/Product/ProductDetail/Anchors"
import EyeFitUInjectionPoint from "@/app/(site)/_components/Page/Product/ProductDetail/EyeFitU"
import FitType from "@/app/(site)/_components/Page/Product/ProductDetail/FitType"
import NewAddToCartButton from "@/app/(site)/_components/Page/Product/ProductDetail/NewAddToCartButton"
import CentraRichText from "@/app/(site)/_components/Page/Product/ProductDetail/ProductDescriptionText/CentraRichText"
import { useLocaleFromPathname } from "@/app/(site)/_utils/_hooks/useLocaleFromPathname"
import {
    type MediaItem,
    type ProductData,
    type RelatedVariant,
    type SizeInfo
} from "@/lib/content-service/types/ContentServiceProduct"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { useCookieBot } from "@/src/hooks/useCookieBot"
import { getLangCode } from "@/src/utils/string/getLangCode"

type ProductFormType = {
    relatedVariantsFitType?: string[]
    productFitType?: string
    product: ProductData
    relatedVariants: RelatedProduct[] | RelatedVariant[]
    firstAvailableSize?: SizeInfo
    sizes: SizeInfo[]
    params: { slug: string; locale: string }
    benefitList?: { text: string; addKlaviyoButton: boolean }[]
    featureAnchors?: { anchor: string; title: string }[]
}

export default function ProductForm({
    relatedVariantsFitType,
    productFitType,
    product,
    relatedVariants,
    firstAvailableSize,
    sizes,
    params,
    benefitList,
    featureAnchors
}: ProductFormType) {
    const router = useRouter()
    const pathname = usePathname()
    const langCode = getLangCode(params.locale) || "en"
    const { consent, consentReady } = useCookieBot()
    const siteContext = useSiteContext()

    const [errorStyles, setErrorStyles] = useState(false)
    const [dataForEyeFitUI, setDataForEyeFitUI] = useState<EyeFitUInitData>()
    const [fitType, setFitType] = useState(productFitType || "")
    const hasTrackedView = useRef(false)
    const hasSetInitialSize = useRef(false)
    const previousProductSku = useRef<string>("")
    const previousPathname = useRef<string>("")
    const [ctaButtonHeight, setCtaButtonHeight] = useState<number>(0)
    const stickyButtonRef = useRef<HTMLDivElement>(null)

    const [filteredAllVariants, setFilteredAllVariants] = useState<
        (CheckoutAPIProduct | RelatedProduct | ProductData | RelatedVariant)[]
    >([...relatedVariants, product])
    // Memoize derived values to prevent unnecessary re-renders
    const canonicalCategory = useMemo(
        () =>
            Array.isArray(product?.categoryName)
                ? product.categoryName.map(c => c.toLowerCase()).join("/")
                : "",
        [product?.categoryName]
    )

    const productColorName = product?.color_swatch?.color_name
    const productLensName = product?.lens?.lens_name
    const isOneSize = useMemo(
        () =>
            Array.isArray(sizes) &&
            sizes.length > 0 &&
            sizes.every(({ name }) => name === "One Size" || name === "Regular fit") &&
            sizes.length === 1,
        [sizes]
    )

    const isFitType = useMemo(
        () => Array.isArray(relatedVariantsFitType) && relatedVariantsFitType.length > 0,
        [relatedVariantsFitType]
    )

    const sizeLabels = useMemo(() => sizes.map(size => size.name) || [], [sizes])

    // Get the currently selected size from the global state (only when needed)
    const selectedSize = useProductStore(state => state.selectedSize)

    // Memoize product SKU to prevent unnecessary effect triggers
    const productSku = useMemo(() => product.sku, [product.sku])

    // Memoize locale to prevent unnecessary effect triggers
    const locale = useLocaleFromPathname()

    // Memoize product properties to prevent unnecessary re-renders
    const productFit = useMemo(() => product?.fit, [product?.fit])
    const productName = useMemo(() => product?.name, [product?.name])
    const productMedia = useMemo(() => product?.media, [product?.media])

    // Reset tracking when product changes (color navigation)
    useEffect(() => {
        if (productSku !== previousProductSku.current) {
            hasTrackedView.current = false
            hasSetInitialSize.current = false
            previousProductSku.current = productSku
        }
    }, [productSku, params.slug])

    const isColorSwatchNavigation =
        typeof window !== "undefined" &&
        localStorage.getItem("navigatingBetweenVariants") === "color_swatching"

    // Reads size/siteContext/consent at fire time so SizeChart auto-select
    // (`selectedSize`) cannot send a second Load PDP. User size clicks still
    // go through SizeChart as "Change Size".
    const trackViewItem = useEffectEvent(async () => {
        const trackingType = isColorSwatchNavigation ? "Change Colour" : "Load PDP"
        const sizeForTracking = selectedSize || firstAvailableSize?.name || undefined

        if (!hasTrackedView.current) {
            hasTrackedView.current = true

            const [{ viewItemEvent }, { extractProductHierarchy }] = await Promise.all([
                import("@/lib/utils/tracking/gtmEvents"),
                import("@/lib/utils/tracking/productHierarchyUtils")
            ])

            const success = await viewItemEvent(
                product,
                { size: sizeForTracking },
                siteContext,
                trackingType,
                extractProductHierarchy(product)
            )

            if (!success) {
                hasTrackedView.current = false
                return
            }

            hasSetInitialSize.current = true
            previousProductSku.current = productSku
            previousPathname.current = pathname

            if (isColorSwatchNavigation) {
                localStorage.removeItem("navigatingBetweenVariants")
            }
        }

        if (consent.necessary) {
            viewItemDepictEvent(product, { size: sizeForTracking })
        }
    })

    // Handle initial load and color swatch navigation.
    // Bucket B migration: previously gated on `basketSession?.token` to wait until
    // a Centra session existed before firing the view event. Post-Phase-2 the
    // session is created lazily on first add-to-cart, so the gate would silently
    // suppress tracking for every browsing visitor. `siteContext` (URL-derived)
    // already carries the country/language fields GA4 needs; no Centra signal
    // required for the event itself.
    useEffect(() => {
        if (!product?.sku || !product?.name) {
            return
        }

        void trackViewItem()
    }, [productSku, pathname, isColorSwatchNavigation, params.slug, consent.necessary])

    const handleFitTypeChange = useCallback(
        (newFitType: string) => {
            if (product?.fit_type_fit_type !== newFitType) {
                const relevantProduct = (relatedVariants as RelatedVariant[]).find(
                    p => p?.fit_type_fit_type === newFitType
                )
                const newUri = relevantProduct?.uri

                if (newUri) {
                    router.replace(newUri, {
                        // @ts-expect-error: shallow is not in NavigateOptions
                        shallow: true
                    })
                }
            }
        },
        [product?.fit_type_fit_type, relatedVariants, router]
    )

    useEffect(() => {
        handleFitTypeChange(fitType)
    }, [fitType, handleFitTypeChange])

    useEffect(() => {
        if (product?.categoryName && product.categoryName?.length > 0) {
            setDataForEyeFitUI({
                // Last category in the array is the most relevant
                canonicalCategory: canonicalCategory ?? "",
                gender: productFit?.[0]?.label ?? "",
                sizeLabels: sizeLabels ?? "",
                mainImage: (productMedia as MediaItem[])?.[0]?.url || placeholder?.src,
                productName: productName ?? "",
                // Functional/preferences consent gates the full EyeFitU experience
                // (recommender + size guides). Without it, only the limited experience
                // (size guides) loads. Re-derived when consent changes, which re-fires
                // eyefitu_init via the hook below. The EyeFitU GTM tag reads this
                // `recommender` flag to force its size-chart-only path when false.
                recommender: consent.preferences,
                ...(product.englishSizeGuideType && {
                    sizeGuideType: product.englishSizeGuideType
                }),
                ...(product.englishSizeGuideName && {
                    sizeGuideName: product.englishSizeGuideName
                })
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        product.productSku,
        canonicalCategory,
        sizeLabels,
        productFit,
        productName,
        productMedia,
        product.englishSizeGuideType,
        product.englishSizeGuideName,
        consent.preferences
    ])

    useEffect(() => {
        const updateCtaButtonHeight = () => {
            if (stickyButtonRef.current) {
                const height = stickyButtonRef.current.offsetHeight
                setCtaButtonHeight(height)
            }
        }
        updateCtaButtonHeight()
        window.addEventListener("resize", updateCtaButtonHeight)

        return () => {
            window.removeEventListener("resize", updateCtaButtonHeight)
        }
    }, [])

    useEyeFitUIEvent(dataForEyeFitUI || null, consentReady)

    return (
        <form className="relative">
            {/* fit_type_fit_type */}
            {isFitType && (
                <div className="flex gap-8 py-16">
                    <Text size="small" weight="regular">
                        {t(langCode, "product_page.fit")}:{" "}
                    </Text>
                    <Text size="small" weight="regular" className="text-main-dark-grey">
                        {productFitType}
                    </Text>
                    <FitType
                        fitType={fitType}
                        setFitType={setFitType}
                        relatedVariantsFitType={relatedVariantsFitType || []}
                        relatedVariants={relatedVariants}
                        setFilteredAllProducts={setFilteredAllVariants}
                    />
                </div>
            )}

            {/* Color Swatches */}
            <div className="border-b border-b-col-border pb-20 pl-12 pr-0 sm:pl-16 lg:px-24 lg:pb-24">
                <p
                    className={`flex items-baseline gap-4 ${product?.lens ? "pb-2" : "pb-8 lg:pb-16"}`}
                >
                    <GCTextElement
                        text={t(langCode, "product_page.color")}
                        as="span"
                        className="h-18 Trim_Mono_POC_Text_12_130_-3_400_uppercase"
                    />
                    <GCTextElement
                        text={productColorName || ""}
                        as="span"
                        className="h-18 text-col-text-secondary Trade_Gothic_Text_12_150_0_300"
                    />
                </p>
                {product?.lens && (
                    <p className="flex items-baseline gap-4 pb-8 lg:pb-16">
                        <GCTextElement
                            text={t(langCode, "product_page.lens")}
                            as="span"
                            className="h-18 Trim_Mono_POC_Text_12_130_-3_400_uppercase"
                        />

                        <GCTextElement
                            text={productLensName || ""}
                            as="span"
                            className="h-18 text-col-text-secondary Trade_Gothic_Text_12_150_0_300"
                        />
                    </p>
                )}
                {/* Image swatches */}
                <ColorSwatches
                    initialProductValue={filteredAllVariants}
                    sizes={sizes}
                    currentProduct={product}
                    langCode={langCode}
                />
            </div>

            {/* Size Selection */}
            <div className="flex flex-col gap-12 border-b border-b-col-border px-12 pb-32 pt-20 sm:px-16 temp-md:gap-20 temp-md:pb-24 lg:gap-12 lg:px-24 lg:pt-32">
                <div className="flex h-24 items-center justify-between">
                    <GCTextElement
                        text={t(langCode, "product_page.size")}
                        as="span"
                        className="Trim_Mono_POC_Text_12_130_-3_400_uppercase"
                    />
                    <div className="flex items-center gap-8">
                        <EyeFitUInjectionPoint
                            productId={product.sku}
                            productName={product.name}
                        />
                    </div>
                </div>
                <SizeChart
                    sizes={sizes}
                    setErrorStyles={setErrorStyles}
                    errorStyles={errorStyles}
                    firstAvailableSize={firstAvailableSize}
                    currentProduct={product}
                    locale={locale}
                    isOneSize={isOneSize}
                />
            </div>

            {/* Dsktop Add to Cart Button */}
            <div className="hidden flex-col gap-12 px-16 pb-20 pt-16 lg:flex lg:px-24 lg:pb-12 lg:pt-24">
                <NewAddToCartButton
                    product={product}
                    setErrorStyles={setErrorStyles}
                    langCode={langCode}
                    sizes={sizes}
                    firstAvailableSize={firstAvailableSize}
                />
                {benefitList && benefitList.length > 0 && (
                    <ProductUsps usps={benefitList} langCode={langCode} />
                )}
            </div>

            {/* Mobile Add to Cart Button */}
            <div
                ref={stickyButtonRef}
                className={`sticky z-20 flex w-full flex-col gap-12 bg-col-bg-primary px-12 pb-20 pt-16 lg:hidden`}
                style={{
                    top: `calc(100dvh - ${ctaButtonHeight}px)`
                }}
            >
                <NewAddToCartButton
                    product={product}
                    setErrorStyles={setErrorStyles}
                    langCode={langCode}
                    sizes={sizes}
                    firstAvailableSize={firstAvailableSize}
                />
                {benefitList && benefitList.length > 0 && (
                    <ProductUsps usps={benefitList} langCode={langCode} />
                )}
            </div>

            {/* Text excerpt and Anchors Mobile*/}
            <div
                style={{
                    marginTop: `-${ctaButtonHeight}px`,
                    paddingBottom: `${ctaButtonHeight}px`
                }}
                className="lg:hidden"
            >
                {product.excerptHtml && (
                    <CentraRichText
                        content={product.excerptHtml}
                        className="relative z-10 block px-12 pb-40 pt-24 text-col-text-primary Trade_Gothic_Text_16_150_0_300 lg:hidden"
                    />
                )}

                <div className="relative z-10 block border-b border-b-col-border px-12 pb-24 text-col-text-secondary lg:hidden lg:pb-0">
                    {featureAnchors && featureAnchors.length > 0 && (
                        <Anchors featureAnchors={featureAnchors} />
                    )}
                </div>
            </div>
        </form>
    )
}
